import { useEffect, useId, useRef, useState } from 'react'
import { changeKey, snapshotObject, type Change, type Frame, type SceneObject, type Snapshot } from './floorReview.logic'
import { distToSegmentSquared } from '../../utils/traySegregation.logic'

export function FloorReviewMap({ snapshot, objects, title, frame, changes, selected, onSelect, onCamera, layers }: {
  snapshot: Snapshot; objects: SceneObject[]; title: string; frame: Frame; changes: Change[]; selected: string | null
  onSelect: (key: string) => void; onCamera: (frame: Frame) => void
  layers: { grid: boolean; labels: boolean; pathway: boolean; planning: boolean }
}) {
  const gridId = useId()
  const svgRef = useRef<SVGSVGElement>(null)
  const drag = useRef<{ x: number; y: number; frame: Frame } | null>(null)
  const [size, setSize] = useState({ w: 400, h: 400 })
  const [hitPoint, setHitPoint] = useState<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const observer = new ResizeObserver(([entry]) => setSize({ w: entry.contentRect.width, h: entry.contentRect.height }))
    observer.observe(svg)
    return () => observer.disconnect()
  }, [])
  const scale = Math.max(0.001, Math.min(size.w / frame.w, size.h / frame.h))
  const floor = snapshot.floor.settings.floorPlan
  const tile = floor ? floor.tileSizeMm / 1000 : 0
  const changeByKey = new Map(changes.map(change => [changeKey(change), change]))
  const active = objects.find(object => object.key === selected)
  const caption = (object: SceneObject) => object.name + (object.kind.startsWith('pathway') ? ' · ID ' + object.key.slice(object.key.indexOf(':') + 1) : '') + (object.description && object.description !== object.name ? ' · ' + object.description : '')
  const selectionLabel = active && caption(active) || (!selected ? 'Нет выбранного объекта' : snapshotObject(snapshot, selected) ? 'Нет геометрии объекта' :
    ['rack', 'row', 'pathway_node', 'pathway_edge', 'aisle', 'structure', 'planned_placement'].includes(selected.split(':')[0]) ? 'Объект отсутствует' : 'Данные без геометрии')
  const visible = objects.filter(object => (layers.pathway || !object.kind.startsWith('pathway')) && (layers.planning || object.kind !== 'planned_placement'))
  // Preserve exact physical geometry; disambiguation happens at the clicked point.
  const nearby = hitPoint ? visible.filter(object => object.kind === 'pathway_edge' && object.points?.length === 2 &&
    distToSegmentSquared(hitPoint, object.points[0], object.points[1]) <= (8 / scale) ** 2) : []
  const coincident = active?.kind === 'pathway_edge' ? visible.filter(object => object.kind === 'pathway_edge' && object.points?.every((point, index) =>
    point.x === active.points?.[index].x && point.y === active.points?.[index].y) ||
    object.kind === 'pathway_edge' && object.points?.every((point, index) => point.x === active.points?.[1 - index].x && point.y === active.points?.[1 - index].y)) : []
  const candidates = nearby.some(object => object.key === selected) ? nearby : coincident
  const estimated = (object: SceneObject) => {
    if (object.kind !== 'rack') return false
    const rack = snapshotObject(snapshot, object.key)
    return rack?.width_m == null || rack?.depth_m == null
  }
  const estimatedCount = visible.filter(estimated).length
  const props = (object: SceneObject) => ({
    'data-selected': String(selected === object.key), 'data-change': changeByKey.get(object.key)?.change,
    'data-kind': object.kind, 'data-media': object.media, 'data-status': object.status,
    'data-estimated': String(estimated(object)),
    'aria-label': object.kind === 'rack' ? 'Стойка ' + caption(object) + (estimated(object) ? ' · контур оценочный' : '') : object.kind === 'planned_placement' ? 'Цель переноса: ' + object.name : caption(object),
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation()
      const transform = svgRef.current?.getScreenCTM()
      setHitPoint(object.kind === 'pathway_edge' && transform ? new DOMPoint(event.clientX, event.clientY).matrixTransform(transform.inverse()) : null)
      onSelect(object.key)
    },
  })
  return <figure className="floor-review-scene" data-side={title === 'До' ? 'before' : 'after'}>
    <figcaption><strong>{title}</strong><span title={selectionLabel}>{selectionLabel}</span>{candidates.length > 1 && <select aria-label={(nearby.some(object => object.key === selected) ? 'Магистрали в точке ' : 'Совпадающие магистрали ') + title.toLowerCase()} value={selected || ''} onChange={event => onSelect(event.target.value)}>{candidates.map(object => <option key={object.key} value={object.key}>{caption(object)}</option>)}</select>}</figcaption>
    <svg ref={svgRef} aria-label={'План ' + (title === 'До' ? 'до' : 'после')} viewBox={[frame.x, frame.y, frame.w, frame.h].join(' ')} preserveAspectRatio="xMidYMid meet"
      onWheel={event => {
        event.preventDefault()
        const factor = event.deltaY > 0 ? 1.2 : 1 / 1.2
        const w = Math.max(0.2, Math.min(100000, frame.w * factor)), h = w * frame.h / frame.w
        onCamera({ x: frame.x + (frame.w - w) / 2, y: frame.y + (frame.h - h) / 2, w, h })
      }}
      onPointerDown={event => {
        if (event.button !== 0) return
        if ((event.target as Element).closest('g[data-kind]')) return
        drag.current = { x: event.clientX, y: event.clientY, frame }
        event.currentTarget.setPointerCapture(event.pointerId)
      }}
      onPointerMove={event => {
        const start = drag.current
        if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) < 4) return
        onCamera({ ...start.frame, x: start.frame.x - (event.clientX - start.x) / scale, y: start.frame.y - (event.clientY - start.y) / scale })
      }}
      onPointerUp={() => { drag.current = null }}
      onPointerCancel={() => { drag.current = null }}>
      {floor && <><defs><pattern id={gridId} width={tile} height={tile} x={floor.originXMm / 1000} y={floor.originYMm / 1000} patternUnits="userSpaceOnUse">
        <path d={'M ' + tile + ' 0 H 0 V ' + tile} className="review-grid" />
      </pattern></defs><rect className="review-floor" x={floor.originXMm / 1000} y={floor.originYMm / 1000} width={floor.widthMm / 1000} height={floor.heightMm / 1000} fill={layers.grid && tile * scale >= 12 ? 'url(#' + gridId + ')' : 'none'} /></>}
      {visible.filter(object => object.rect).map(object => {
        const rect = object.rect!
        return <g key={object.key} {...props(object)}>
          <g transform={'rotate(' + rect.rotation + ',' + (rect.x + rect.w / 2) + ',' + (rect.y + rect.h / 2) + ')'}>
            <rect className={'review-' + object.kind} x={rect.x} y={rect.y} width={rect.w} height={rect.h} data-reserved={String(!!object.reserved)} />
            {object.kind === 'rack' && <line className="review-facing" x1={rect.x + rect.w * 0.2} y1={rect.y} x2={rect.x + rect.w * 0.8} y2={rect.y} />}
          </g>
          {layers.labels && object.kind === 'rack' && rect.w * scale >= object.name.length * 6.7 && <text className="review-label" x={rect.x + rect.w / 2} y={rect.y + rect.h + 16 / scale} fontSize={11 / scale} textAnchor="middle">{object.name}</text>}
        </g>
      })}
      {visible.filter(object => object.points && object.points.length === 2).sort((a, b) => Number(a.key === selected) - Number(b.key === selected)).map(object => {
        const [a, b] = object.points!, changed = changeByKey.get(object.key)
        return <g key={object.key} {...props(object)}>
          <line className={'review-' + object.kind} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
          <line className="review-hit" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
          {changed && <circle className="review-change-marker" cx={(a.x + b.x) / 2} cy={(a.y + b.y) / 2} r={4 / scale} />}
        </g>
      })}
      {visible.filter(object => object.points && object.points.length === 1).map(object => <g key={object.key} {...props(object)}><circle className="review-node" cx={object.points![0].x} cy={object.points![0].y} r={4 / scale} /></g>)}
    </svg>
    <div className="review-map-scale">{floor ? 'Плита ' + floor.tileSizeMm + ' мм' : 'Размер плит не задан'} · {Math.round(frame.w * 10) / 10} × {Math.round(frame.h * 10) / 10} м</div>
    {estimatedCount > 0 && <div className="review-map-scale" role="status">Оценочных контуров: {estimatedCount} · физическое размещение не подтверждено</div>}
  </figure>
}
